import clsx from 'clsx';
import React from 'react';
import { AppLayoutButton, CloseButton, togglesConfig } from './toggles';
import { CircularButton } from './toggles/circular-button';
import styles from './styles.css.js';
import { useHasRendered } from '../internal/hooks/use-has-rendered';
// We are using two landmarks per drawer, i.e. two NAVs and two ASIDEs, because of several
// known bugs in NVDA that cause focus changes within a container to sometimes not be
// announced. As a result, we use one region for the open button and one region for the
// actual drawer content, always hiding the other one when it's not visible.
// An alternative solution to follow a more classic implementation here to only have one
// button that triggers the opening/closing of the drawer also did not work due to another
// series of bugs in NVDA (together with Firefox) which prevent the changed expanded state
// from being announced.
// Even with this workaround in place, the announcement of the close button when opening a
// panel in NVDA is not working correctly. The suspected root cause is one of the bugs below
// as well.
// Relevant tickets:
// * https://github.com/nvaccess/nvda/issues/6606
// * https://github.com/nvaccess/nvda/issues/5825
// * https://github.com/nvaccess/nvda/issues/5247
// * https://github.com/nvaccess/nvda/pull/8869 (reverted PR that was going to fix it)
export function Drawer(_a) {
    var _b;
    var visualRefresh = _a.visualRefresh, contentClassName = _a.contentClassName, toggleClassName = _a.toggleClassName, closeClassName = _a.closeClassName, width = _a.width, type = _a.type, toggleRefs = _a.toggleRefs, topOffset = _a.topOffset, bottomOffset = _a.bottomOffset, ariaLabels = _a.ariaLabels, children = _a.children, isOpen = _a.isOpen, isMobile = _a.isMobile, onToggle = _a.onToggle, onClick = _a.onClick;
    var _c = togglesConfig[type], TagName = _c.TagName, iconName = _c.iconName, getLabels = _c.getLabels;
    var _d = getLabels(ariaLabels), mainLabel = _d.mainLabel, closeLabel = _d.closeLabel, openLabel = _d.openLabel;
    var drawerWidthOpen = isMobile ? undefined : width;
    var drawerWidthClosed = visualRefresh ? 0 : undefined;
    var drawerWidth = isOpen ? drawerWidthOpen : drawerWidthClosed;
    var drawerContentWidth = visualRefresh && !isMobile ? width : drawerWidth;
    var hasRendered = useHasRendered();
    var visualRefreshOpenButton = (React.createElement(TagName, { "aria-label": mainLabel, className: styles['visual-refresh-toggle'], "aria-hidden": isOpen },
        React.createElement(CircularButton, { fixed: true, side: type === 'tools' ? 'right' : 'left', style: { top: (topOffset !== null && topOffset !== void 0 ? topOffset : 0) + 20 }, ref: toggleRefs.toggle, className: toggleClassName, iconName: iconName, ariaLabel: openLabel, onClick: function () { return onToggle(true); }, ariaExpanded: false })));
    var regularOpenButton = (React.createElement(TagName, { "aria-label": mainLabel, className: styles.toggle, "aria-hidden": isOpen },
        React.createElement(AppLayoutButton, { ref: toggleRefs.toggle, className: toggleClassName, iconName: iconName, ariaLabel: openLabel, onClick: function () { return onToggle(true); }, ariaExpanded: false })));
    return (React.createElement("div", { className: clsx(styles.drawer, (_b = {},
            _b[styles['drawer-closed']] = !isOpen,
            _b[styles['drawer-mobile']] = isMobile,
            _b[styles['drawer-refresh']] = visualRefresh,
            _b[styles.mounted] = hasRendered,
            _b)), style: { width: drawerWidth }, onClick: function (event) {
            if (onClick) {
                onClick(event);
            }
            // to prevent calling onToggle from the drawer when it's called from the toggle button
            if (!isOpen && event.target.tagName !== 'BUTTON') {
                onToggle(true);
            }
        } },
        !isMobile && visualRefresh && visualRefreshOpenButton,
        React.createElement("div", { style: { width: drawerContentWidth, top: topOffset, bottom: bottomOffset }, className: clsx(styles['drawer-content'], contentClassName) },
            !isMobile && !visualRefresh && regularOpenButton,
            React.createElement(TagName, { "aria-label": mainLabel, "aria-hidden": !isOpen },
                React.createElement(CloseButton, { ref: toggleRefs.close, className: closeClassName, ariaLabel: closeLabel, onClick: function () { return onToggle(false); } }),
                children))));
}
//# sourceMappingURL=drawer.js.map